import { Component, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { Aspect, CompetencyLevel, ValidityOptions } from '../../models/evaluation.model';
import { ElementRef } from '@angular/core';
import { CompetencyButtonComponent } from '../competency-button/competency-button.component';
import { RichTextEditorComponent } from '../rich-text-editor/rich-text-editor.component';
import { StatusIconComponent } from '../status-icons/status-icon.component';
import { MatIconModule } from '@angular/material/icon';
import { debounce } from 'lodash';

interface CompetencyLevelDetail {
	label: string;
	icon: string;
	class: string;
	requiresSelection?: boolean;
	requiresFeedback?: boolean;
	[key: string]: any; // Add index signature to allow string indexing
}

interface CompetencyLevelMap {
	[key: string]: CompetencyLevelDetail;
}

const COMPETENCY_LEVELS: CompetencyLevelMap = {
	['Competent' as CompetencyLevel]: {
		label: 'Competent',
		icon: 'check_small',
		class: 'rating-competent',
		requiresFeedback: false,
	},
	['Approaching Competence' as CompetencyLevel]: {
		label: 'Approaches',
		icon: 'priority_high',
		class: 'rating-approaching',
		requiresFeedback: true,
	},
	['Not Evident' as CompetencyLevel]: {
		label: 'Not Evident',
		icon: 'close',
		class: 'rating-not-evident',
		requiresFeedback: true,
	},
	['pending']: {
		label: 'Pending',
		icon: 'question_mark',
		class: '',
		requiresSelection: true,
		requiresFeedback: false,
	},
	['Completed']: {
		label: 'Completed',
		icon: 'lock',
		class: 'rating-completed',
		requiresFeedback: false,
		requiresSelection: false,
	},
};

@Component({
	selector: 'app-aspect-evaluation',
	standalone: true,
	imports: [
		FormsModule,
		CommonModule,
		CompetencyButtonComponent,
		StatusIconComponent,
		MatIconModule,
		RichTextEditorComponent,
	],
	templateUrl: './aspect-evaluation.component.html',
	styleUrls: ['./aspect-evaluation.component.scss'],
	animations: [
		trigger('collapseAnimation', [
			state('expanded', style({ height: '*', opacity: 1 })),
			state('collapsed', style({ height: '0', opacity: 0, padding: 0 })),
			transition('expanded <=> collapsed', animate('200ms ease-out')),
		]),
	],
})
export class AspectEvaluationComponent {
	private isFeedbackInitialized = false;
	@Input() aspect!: Aspect;
	@Input() isFirst = false;
	@Input() isLast = false;
	@Output() aspectChange = new EventEmitter<Aspect>();
	@Output() accordionToggle = new EventEmitter<void>();
	@Output() navigate = new EventEmitter<'next' | 'previous' | 'finished'>();
	@Output() isTyping = new EventEmitter<void>();
	@ViewChild('feedback') feedbackTextarea!: ElementRef;
	isCollapsed = true;

	hasError: boolean = false;
	showError: boolean = true;
	errorMessage: string = '';

	get hasVisualError() {
		return this.hasError && this.showError;
	}

	constructor(private elementRef: ElementRef) {}

	get canNavigateNext() {
		if (this.aspect.status.name === 'Locked') {
			return true;
		}

		if (this.aspect.competencyLevel) {
			if (
				this.aspect.competencyLevel === 'Approaching Competence' ||
				this.aspect.competencyLevel === 'Not Evident'
			) {
				return this.hasValidStudentFeedback();
			} else {
				return true;
			}
		}
		return;
	}

	get competencyLevelDetails() {
		if (this.aspect.status.name === 'Locked') {
			return COMPETENCY_LEVELS['Completed'];
		}

		if (this.aspect.competencyLevel) {
			return COMPETENCY_LEVELS[this.aspect.competencyLevel];
		}
		return COMPETENCY_LEVELS['pending'];
	}

	get showAIFeedback() {
		return ['FULL', 'PARTIAL'].includes(this.aspect.suitability ?? '');
	}

	get canEvaluateAspect() {
		return this.aspect.status.name === 'Open';
	}

	get aspectCompletedMessage() {
		switch (this.aspect.status.value) {
			case 'grammarly-completed':
				return {
					title: 'Professional Communication Auto-scored',
					body: 'The Profession Communication aspect has already been scored. Evaluators cannot override auto-scored PC aspects.',
				};
			default:
				return {
					title: 'Previously Demonstrated',
					body: 'The candidate has shown competency in this aspect in a previous submission. No further evaluator review is requested.',
				};
		}
	}

	ngOnDestroy() {
		// Cancel any pending debounced calls when component is destroyed
		this.debouncedSafeFeedbackProgress.cancel();
	}

	onAccordionToggle(): void {
		this.isCollapsed = !this.isCollapsed;
		if (this.isCollapsed) {
			// Report validity when accordion is closed
			this.handleAccordionToggleValidity();
		}
		this.accordionToggle.emit();
	}

	hasValidStudentFeedback() {
		const temp = document.createElement('div');
		temp.innerHTML = this.aspect.feedback ? this.aspect.feedback : '';

		// Get text content and trim whitespace
		const visibleText = temp.textContent || temp.innerText || '';
		return visibleText.trim().length > 0;
	}

	onNavigate(direction: 'next' | 'previous' | 'finished'): void {
		if (direction === 'previous') {
			this.handleAccordionToggleValidity();
		} else {
			this.reportValidity();
		}
		if (this.isValid() || direction === 'previous') {
			if (direction === 'next' && this.isLast) {
				this.navigate.emit('finished');
			} else {
				this.navigate.emit(direction);
			}
		}
	}

	handleAccordionToggleValidity() {
		if (this.hasValidStudentFeedback() && !this.aspect.competencyLevel) {
			this.reportValidity();
		} else {
			this.reportValidity({
				ignoredValidationRules: ['requiresSelection'],
			});
		}
	}

	setCompetencyLevel(level: CompetencyLevel): void {
		if (this.aspect.competencyLevel === level) {
			this.aspect.competencyLevel = undefined;
		} else {
			this.aspect.competencyLevel = level;
			if (level === 'Approaching Competence' || level === 'Not Evident') {
				this.feedbackTextarea.nativeElement.scrollIntoView({
					block: 'nearest',
					behavior: 'smooth',
				});

				setTimeout(() => {
					this.feedbackTextarea.nativeElement.focus();
				}, 300);
			}
		}
		this.reportValidity({ ignoredValidationRules: ['requiresFeedback'] });
		this.aspectChange.emit(this.aspect);
	}

	debouncedSafeFeedbackProgress = debounce(() => {
		this.aspectChange.emit(this.aspect);
	}, 2000);

	onFeedbackChange(feedback: string): void {
		if (!this.isFeedbackInitialized) {
			this.isFeedbackInitialized = true;
			return;
		}
		this.aspect.feedback = feedback;
		this.reportValidity();
		this.isTyping.emit();
		this.debouncedSafeFeedbackProgress();
	}

	onBlur() {
		this.debouncedSafeFeedbackProgress.cancel();
		this.aspectChange.emit(this.aspect);
	}

	getStatus(): CompetencyLevel | 'pending' {
		return this.aspect.status.name === 'Locked'
			? 'Competent'
			: this.aspect.competencyLevel || 'pending';
	}

	scrollIntoViewIfOpen() {
		// if (!this.isCollapsed) {
		// Allow state to update and wait until after animation before scrolling
		setTimeout(() => {
			this.elementRef.nativeElement.scrollIntoView({
				behavior: 'smooth',
				block: 'start',
			});
		});
		// }
	}

	setCollapsed(value: boolean): void {
		this.isCollapsed = value;
		this.accordionToggle.emit();
		// this.scrollIntoViewIfOpen();
	}

	public reportValidity({
		ignoredValidationRules = [],
		ignoredCompetencyLevels = [],
	}: ValidityOptions = {}) {
		// The "Validity Options" ONLY affect the visual elements of the validation
		if (this.aspect.status.name === 'Open') {
			this.showError = true;
			const ignoredRules = { ...this.competencyLevelDetails };
			ignoredValidationRules.forEach(x => {
				ignoredRules[x] = false;
			});

			if (ignoredCompetencyLevels.includes(this.getStatus())) {
				this.showError = false;
			}

			if (this.competencyLevelDetails.requiresSelection) {
				if (!ignoredRules.requiresSelection) {
					this.showError = false;
				}
				this.hasError = true;
				this.errorMessage = 'A selection is required.';
			} else if (
				this.competencyLevelDetails.requiresFeedback &&
				!this.hasValidStudentFeedback()
			) {
				if (!ignoredRules.requiresFeedback) {
					this.showError = false;
				}
				this.hasError = true;
				this.errorMessage = 'You are required to leave feedback for the student.';
			} else {
				this.hasError = false;
				this.errorMessage = '';
			}
		} else {
			this.hasError = false;
			this.errorMessage = '';
		}
	}

	public isValid() {
		return !this.hasError;
	}
}
